<template>
    <main class="data-table">
        <locale-priority
            v-if="filters.isLevels && levelsLoaded"
            :initial-list="levelsList"
            :currentLevel="currentLevel"
            @setLevel="setLevel"
        />

        <v-data-table
            :headers="headers"
            :items="list"
            :total-items="30"
            :loading="loading"
            :pagination.sync="sync"
            :hide-actions="true"
            :must-sort="true"
            :no-data-text="$t('message.generics.message.noData')"
            :no-results-text="$t('message.generics.message.noData')"
            sort-icon="mdi-chevron-down"
            ref="table"
        >
            <v-progress-linear
                slot="progress"
                :color="providerColor"
                indeterminate
            />

            <template slot="items" slot-scope="props">
                <app-advertising-table-item
                    :dataItem="props.item"
                    :key="itemKey(props.item)"
                    :sortable="sortable"
                    :show-order-column="shouldDisplayOrderColumn"
                    :show-standard-actions="shouldDisplayStandardActions"
                    :show-last-updated-column="showLastUpdated"
                    :is-all-campaigns="isAllCampaigns"
                    @change="load"
                />
            </template>
        </v-data-table>

        <v-pagination
            v-if="totalPages"
            v-model="sync.page"
            :color="getColor(palette.primary)"
            :length="totalPages"
            @input="setPage"
        />
    </main>
</template>

<script>
import LocalePriority from "@views/locals/components/LocalePriority";
import EventBus from "@packages/event-bus";
import sortableTable from "@mixins/sortable-table";
import paginatedTable from "@mixins/paginated-table";
import AppAdvertisingTableItem from "./components/AdvertisingTableItem";
import provider from "@mixins/provider";
import colors from "@mixins/colors";

const ALL_CAMPAIGNS_PAGE_SIZE = 50;

export default {
    components: {
        AppAdvertisingTableItem,
        LocalePriority,
    },

    data() {
        return {
            levelsLoaded: false,
            levelsList: [],
            entity: "advertising",
            currentLevel: 1,
        };
    },

    computed: {
        headers() {
            const items = [
                { text: "", value: null, sortable: false },
                { text: "", value: null, sortable: false },
                {
                    text: this.$t("message.generics.message.status"),
                    value: "isAtivo",
                    width: "30px",
                },
            ];

            if (this.shouldDisplayOrderColumn) {
                items.push({
                    text: this.$t("message.campaigns.message.order"),
                    value: "ordem",
                    width: "60px",
                });
            }

            items.push(
                ...[
                    {
                        text: this.$t("message.campaigns.message.campaign"),
                        value: "nome",
                    },
                    {
                        text: this.$t("message.generics.message.startDate"),
                        value: "dataInicio",
                    },
                    {
                        text: this.$t("message.generics.message.endDate"),
                        value: "dataFim",
                    },
                ]
            );

            if (this.showLastUpadated) {
                items.push({
                    text: "Data Última Alteração",
                    value: "dataUltimaAlteracao",
                    sortable: true,
                });
            }

            return [
                ...items,
                {
                    text: this.$t("message.admGenerics.message.edition"),
                    value: "repetir",
                    sortable: false,
                },
            ];
        },
        isEstablishment() {
            return this.$store.state.users.user.tipo === "ESTABELECIMENTO";
        },
        isAnunciante() {
            return this.$store.state.users.user.tipo === "ANUNCIANTE";
        },
        filters() {
            return this.$store.state.advertising.filters;
        },
        isAllCampaigns() {
            return this.$store.state.advertising.isAllCampaigns;
        },
        isThirdParty() {
            return this.filters.thirdParty;
        },
        sortable() {
            return this.isAnunciante
                ? !this.filters.thirdParty
                : this.filters.isLevels;
        },
        user() {
            return this.$store.state.users.user;
        },

        isMasterUser() {
            return this.user && this.user.permissions.includes("MASTER");
        },
        shouldDisplayOrderColumn() {
            return (
                this.isEstablishment &&
                this.filters.isLevels &&
                this.currentLevel !== 0
            );
        },
        shouldDisplayStandardActions() {
            if (!this.isAllCampaigns && !this.filters.isLevels) return true;
            if (this.isAllCampaigns && this.isMasterUser) {
                return true;
            }
            return false;
        },
        showLastUpdated() {
            return this.isAnunciante && this.isThirdParty;
        },
        method() {
            if (this.isAllCampaigns) {
                return "getAllCampaigns";
            }

            return this.filters.isLevels ? "levelsList" : "list";
        },
        amountPerPage() {
            return this.isAllCampaigns ? ALL_CAMPAIGNS_PAGE_SIZE : undefined;
        },
        countMethod() {
            return this.isAllCampaigns ? "countAllCampaigns" : undefined;
        },
        orderMethod() {
            return this.filters.isLevels ? "levelOrder" : "order";
        },
    },

    watch: {
        async filters() {
            this.orderParams.level = this.filters.level;
        },
        isThirdParty() {
            if (this.showLastUpadated) {
                this.$store.commit("ADVERTISING_SET_FILTER", {
                    key: "desc",
                    value: true,
                });
            }
        },
    },

    mixins: [paginatedTable, sortableTable, provider, colors],

    async created() {
        this.resetAdvertisingList();
        try {
            const isEstabelecimento =
                this.$store.state.users.user.tipo === "ESTABELECIMENTO";

            if (isEstabelecimento) {
                this.levelsList = await this.$api.advertising.levels();
                if (this.levelsList.length > 0) {
                    this.currentLevel = this.levelsList[0].level;
                    this.$store.commit("ADVERTISING_SET_FILTER", {
                        key: "level",
                        value: this.levelsList[0].level,
                    });
                } else {
                    this.load();
                }
                this.levelsLoaded = true;
            } else {
                this.load();
            }
        } catch (error) {
            console.log(error);
        }
    },

    mounted() {
        EventBus.$on("load-advertising", this.load);
    },

    beforeDestroy() {
        EventBus.$off("load-advertising", this.load);
    },

    methods: {
        setLevel({ level }) {
            this.currentLevel = level;
            this.$store.commit("ADVERTISING_SET_FILTER", {
                key: "level",
                value: level,
            });
        },
        resetAdvertisingList() {
            this.$store.commit("ADVERTISING_SET_LIST", null);
        },
    },
};
</script>

<style scoped></style>
